'use client';

import { useTranslations } from 'next-intl';
import { useContext, useEffect, useState } from 'react';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { buildAppDeepLink, isMobileAppWebView } from '@/shared/lib/mobile-app';
import { Button } from '@/shared/ui/button';
import './app-return-handler.scss';

/**
 * Shown only when an APPLICATION tenant's order page ends up in a plain mobile
 * browser — which happens when the payment gateway redirects the customer back
 * after a payment started inside the whitelabel app (gateways redirect without
 * a user gesture, so the OS never hands the URL back to the app on its own).
 * Attempts to reopen the app automatically and renders a fallback button,
 * since browsers only honour app deep links from a user gesture reliably.
 */
export const AppReturnHandler = () => {
    const t = useTranslations();
    const { apiPartnerCode, mobileApp } = useContext(AppConfigContext);
    const [deepLink, setDeepLink] = useState<string | null>(null);

    useEffect(() => {
        if (apiPartnerCode !== 'APPLICATION') {
            return;
        }
        if (isMobileAppWebView(navigator.userAgent)) {
            return; // already inside the app's WebView
        }
        if (!/(^|\/)order\//.test(window.location.pathname)) {
            return; // only relevant right after the payment-gateway return
        }

        const isAndroid = /android/i.test(navigator.userAgent);
        const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
        const appId = isAndroid ? mobileApp?.androidPackage : mobileApp?.iosBundleId;
        if ((!isAndroid && !isIos) || !appId) {
            return;
        }

        const link = buildAppDeepLink(appId, window.location, isAndroid ? 'android' : 'ios');
        setDeepLink(link);

        // Best effort — some browsers allow gesture-less deep links; the button
        // below covers the rest.
        window.location.href = link;
    }, [apiPartnerCode, mobileApp]);

    if (!deepLink) {
        return null;
    }

    return (
        <div className="app-return-handler">
            <span className="app-return-handler__text">{t('PAYMENT.CONTINUE_IN_APP')}</span>
            <Button onClick={() => (window.location.href = deepLink)}>{t('PAYMENT.OPEN_APP')}</Button>
        </div>
    );
};
